import { collection, doc, getDoc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { listSubjects, subjectsForClass } from './academicsApi';
import { getStudentClassForProfile } from './studentsApi';

function subjectSlug(subject) {
  return String(subject || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

function markDocId(studentId, subject) {
  return `${studentId}__${subjectSlug(subject)}`;
}

/** Parent: by parentUid. Student: by admno / compound mark docs. */
export async function getMarksForMyChildren(profile) {
  if (!db || !auth?.currentUser) return [];
  if (profile?.role === 'STUDENT') {
    const ids = [...new Set([profile.admno, profile.studentCsvId].filter(Boolean).map(String))];
    const rows = [];
    for (const id of ids) {
      const legacy = await getDoc(doc(db, 'marks', id));
      if (legacy.exists()) rows.push({ id: legacy.id, ...legacy.data() });
    }
    try {
      const cls = profile.class || (await getStudentClassForProfile(profile));
      const subjects = await listSubjects();
      const subjectNames = subjectsForClass(subjects, cls).map((s) => s.name);
      for (const id of ids) {
        for (const subject of subjectNames) {
          const snap = await getDoc(doc(db, 'marks', markDocId(id, subject)));
          if (snap.exists()) rows.push({ id: snap.id, ...snap.data() });
        }
      }
    } catch {
      /* subject/mark lookups are optional */
    }
    const byKey = new Map();
    rows.forEach((r) => byKey.set(r.id, r));
    return [...byKey.values()];
  }
  const marksRef = collection(db, 'marks');
  const q = query(marksRef, where('parentUid', '==', auth.currentUser.uid));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listAllMarks() {
  if (!db) return [];
  const snap = await getDocs(collection(db, 'marks'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Marks for one student (legacy single doc + per-subject docs). */
export async function listMarksForStudent(studentId) {
  if (!db) return [];
  const id = String(studentId || '').trim();
  if (!id) return [];
  const snap = await getDocs(collection(db, 'marks'));
  return snap.docs
    .filter((d) => {
      if (d.id === id) return true;
      if (d.id.startsWith(`${id}__`)) return true;
      return d.data().studentId === id;
    })
    .map((d) => ({ id: d.id, ...d.data() }));
}

export async function saveSubjectMark({ studentId, studentName, className, subject, score, parentUid }) {
  if (!db) throw new Error('Firestore not configured.');
  const sid = String(studentId).trim();
  const subj = String(subject).trim();
  await setDoc(
    doc(db, 'marks', markDocId(sid, subj)),
    {
      studentId: sid,
      studentName,
      class: className,
      subject: subj,
      score: Number(score),
      parentUid: parentUid || '',
      updatedAt: new Date().toISOString(),
    },
    { merge: true }
  );
}

/** @deprecated use saveSubjectMark */
export async function saveMark({ studentId, studentName, className, parentUid, subject, score }) {
  return saveSubjectMark({ studentId, studentName, className, parentUid, subject, score });
}
